import { createFileRoute, redirect } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { OrderSteps } from "@/features/orders/OrderSteps";
import { getOrderDraft, orderDraftKeys } from "@/features/orders/orderDrafts.api";
import { flowFromStored, newFlow } from "@/features/orders/orderFlow";
import { Skeleton } from "@/components/feedback/Skeleton";
import { hasModule, firstAllowedRoute } from "@/lib/auth";

export const Route = createFileRoute("/_shell/orders/new")({
  // Entering is tms_orders_write; a reader goes back to the inbox.
  beforeLoad: ({ context }) => {
    if (context.session.roles.includes("admin") || hasModule(context.session, "tms_orders_write")) return;
    if (hasModule(context.session, "tms_orders")) throw redirect({ to: "/orders" });
    throw redirect({ to: firstAllowedRoute(context.session) });
  },
  // ?draft=<id> picks a half-typed order back up, or lands here from the take-over screen.
  validateSearch: (search: Record<string, unknown>): { draft?: string } =>
    typeof search["draft"] === "string" && search["draft"] ? { draft: search["draft"] } : {},
  component: NewOrderScreen,
});

function NewOrderScreen() {
  const { t } = useTranslation();
  const { draft } = Route.useSearch();

  const query = useQuery({
    queryKey: orderDraftKeys.detail(draft ?? ""),
    queryFn: () => getOrderDraft(draft!),
    enabled: Boolean(draft),
    retry: false,
  });

  if (draft && query.isPending) {
    return (
      <div className="wl-page" data-screen-label="Back-office New Order" aria-busy="true">
        <Skeleton height="var(--space-16)" />
        <div style={{ marginTop: "var(--space-4)" }}>
          <Skeleton height="320px" />
        </div>
      </div>
    );
  }

  // A draft that is gone — thrown away on another screen, or never yours — must not be an
  // error: an empty new order is exactly what the person wanted anyway.
  if (draft && query.isError) {
    return (
      <div className="wl-page" data-screen-label="Back-office New Order">
        <div className="wl-alert wl-alert--warning" role="alert">
          <span>{t("orders.draftGone")}</span>
        </div>
        <div style={{ marginTop: "var(--space-4)" }}>
          <OrderSteps initial={newFlow()} draftId={null} />
        </div>
      </div>
    );
  }

  return (
    <OrderSteps
      key={query.data?.id ?? "new"}
      initial={query.data ? flowFromStored(query.data.payload) : newFlow()}
      draftId={query.data?.id ?? null}
    />
  );
}
